import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { LayerPluginSpec, PluginSpec } from "plugins/plugin-registry"
import * as Icons from "@nova/component-icons"
import { SidePanelListItemAction } from "./GenericLayerListItem"
import { selectAncestorEntitiesByEntityId } from "../slice"
import {
    ImageColorFinderFlight,
    PanoramaFlight,
    VideoColorFinderFlight,
    VideoFlight,
} from "@nova/protobuf"
import { clearSelection } from "../slice.new"
import { activateSidePanelModal } from "../thunks"
import { openColorFinder } from "plugins/ColorFinder/V2/redux/slice"
import { useIsEditor } from "tools/hooks/useIsEditor"
import { openVideoFlight } from "plugins/Video/state/thunks"
import { selectColorFinderFlightById } from "features/entity-caching/flights/selectors"
import { isVideoFlight } from "plugins/ColorFinder/V2/ColorFinderQueries"
import { localVideoMap } from "plugins/shared/useUniversalControl"
import { localImageColorDataMap } from "plugins/ColorFinder/ImageColorFinder/ImageColorFinderSlice"

export function getGenericUseSidePanelListItemActions(spec: LayerPluginSpec) {
    const useVisibilityAndLockActions = getUseSidePanelListItemVisibilityAndLockActions(spec)
    const useSidePanelListItemActions = (id: string) => {
        const entity = useAppSelector(spec.selectSingleEntity(id))
        const visibilityAndLockActions = useVisibilityAndLockActions(id)
        const actions: SidePanelListItemAction[] = []
        actions.push({
            icon: Icons.ZoomTo,
            onClick: () => {
                spec.zoomTo(entity)
            },
        })
        actions.push(...visibilityAndLockActions)
        return actions
    }
    return useSidePanelListItemActions
}

// TODO CONNOR: types
export function getUseSidePanelListItemVisibilityAndLockActions(spec: PluginSpec) {
    const useSidePanelListItemVisibilityActions = (id: string) => {
        const dispatch = useAppDispatch()
        const entity = useAppSelector(spec.selectSingleEntity(id))
        const ancestors = useAppSelector(selectAncestorEntitiesByEntityId(id))
        const isEditor = useIsEditor()
        const hasLockedAncestor = !!ancestors.find((ancestor) => ancestor.locked)
        const hasHiddenAncestor = !!ancestors.find((ancestor) => !ancestor.visible)
        const actions: SidePanelListItemAction[] = []
        if (entity.locked) {
            actions.push({
                icon: Icons.LayerLocked,
                onClick: () => {
                    if (!isEditor) return
                    dispatch(spec.updateAction({ id, updates: { locked: false } }))
                },
                keepVisible: true,
            })
        } else if (hasLockedAncestor) {
            actions.push({
                icon: Icons.Dot,
                onClick: () => {
                    if (!isEditor) return
                    dispatch(spec.updateAction({ id, updates: { locked: true } }))
                },
                keepVisible: true,
            })
        } else if (isEditor) {
            actions.push({
                icon: Icons.LayerUnlocked,
                onClick: () => {
                    dispatch(spec.updateAction({ id, updates: { locked: true } }))
                },
            })
        }
        if (!entity.visible) {
            actions.push({
                icon: Icons.VisibilityEyeOff,
                onClick: () => {
                    if (!isEditor) return
                    dispatch(spec.updateAction({ id, updates: { visible: true } }))
                },
                keepVisible: true,
            })
        } else if (hasHiddenAncestor) {
            actions.push({
                icon: Icons.Dot,
                onClick: () => {
                    if (!isEditor) return
                    dispatch(spec.updateAction({ id, updates: { visible: false } }))
                },
                keepVisible: true,
            })
        } else if (isEditor) {
            actions.push({
                icon: Icons.VisibilityEye,
                onClick: () => {
                    dispatch(spec.updateAction({ id, updates: { visible: false } }))
                },
            })
        } else if (hasLockedAncestor || entity.locked) {
            // pushes the locked icon over even when there is no visibility icon
            actions.push({
                isPlaceholder: true,
            })
        }
        return actions
    }
    return useSidePanelListItemVisibilityActions
}

export function getUseColorfinderListItemActions(
    spec: PluginSpec<ImageColorFinderFlight> | PluginSpec<VideoColorFinderFlight>
) {
    const useVisibilityAndLockActions = getUseSidePanelListItemVisibilityAndLockActions(spec)
    const useColorfinderListItemActions = (id: string) => {
        const dispatch = useAppDispatch()
        const visibilityAndLockActions = useVisibilityAndLockActions(id)
        const flight = useAppSelector(selectColorFinderFlightById(id))

        const buttons = [...visibilityAndLockActions]

        if (isVideoFlight(flight)) {
            if (!flight.job && !localVideoMap[id]) return buttons
        } else {
            if (!flight.images.length && !localImageColorDataMap[id]) return buttons
        }

        return [
            {
                icon: Icons.VideoLibrary,
                onClick: () => {
                    dispatch(openColorFinder({ flightId: id }))
                },
            },
            ...buttons,
        ]
    }
    return useColorfinderListItemActions
}

export function getUseVideoListItemActions(spec: PluginSpec<VideoFlight>) {
    const useVisibilityAndLockActions = getUseSidePanelListItemVisibilityAndLockActions(spec)
    const useVideoListItemActions = (id: string) => {
        const dispatch = useAppDispatch()
        const visibilityAndLockActions = useVisibilityAndLockActions(id)
        return [
            {
                icon: Icons.VideoLibrary,
                onClick: () => {
                    dispatch(clearSelection())
                    dispatch(openVideoFlight({ flightId: id }))
                },
            },
            ...visibilityAndLockActions,
        ]
    }
    return useVideoListItemActions
}

export function getUsePanoramaListItemActions(spec: PluginSpec<PanoramaFlight>) {
    const useVisibilityAndLockActions = getUseSidePanelListItemVisibilityAndLockActions(spec)
    const usePanoramaListItemActions = (id: string) => {
        const dispatch = useAppDispatch()
        const visibilityAndLockActions = useVisibilityAndLockActions(id)
        return [
            {
                icon: Icons.VideoLibrary,
                onClick: () => {
                    dispatch(clearSelection())
                    dispatch(
                        activateSidePanelModal({
                            id,
                            type: "layer",
                            layerContentType: "pano",
                        })
                    )
                },
            },
            ...visibilityAndLockActions,
        ]
    }
    return usePanoramaListItemActions
}
